"use client";

/**
 * Browser-level "are you sure you want to leave?" guard.
 *
 * Registers a `beforeunload` listener while `active` is true so the browser
 * prompts before tab close / reload / cross-origin navigation. Used when
 * in-flight work would be silently lost — an in-progress transcription, or
 * transcript/notes content that has not been exported yet.
 *
 * Caveats:
 * - Modern browsers (Chrome ≥51, Firefox ≥44, Safari) ignore the supplied
 *   `message` and display their own generic prompt; `returnValue` still
 *   needs to be set for legacy browsers and to satisfy the spec.
 * - This only catches full-document navigation (close, reload, cross-origin
 *   link, address-bar typing). In-app Next.js `router.push` does NOT trip
 *   `beforeunload` — those call sites must add their own `window.confirm`
 *   guard, since the App Router exposes no first-class navigation
 *   interception API.
 */

import { useEffect } from "react";

/**
 * @param active   When true, register the listener. Toggle to off (e.g.
 *                 after the upload completes) to stop prompting.
 * @param message  Legacy-browser prompt text. Modern browsers ignore it
 *                 but the spec still requires `returnValue` be set.
 */
export function useUnloadWarning(active: boolean, message?: string): void {
  useEffect(() => {
    if (!active) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // returnValue must be set for legacy browsers; modern browsers show
      // their own generic prompt regardless of the string content.
      e.returnValue = message ?? "";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [active, message]);
}
